'use client';

import type { AttributesEntity } from '@eliadmin/core/src/api/products/types/product-types';
import { ChoiceChip } from '@eliadmin/core/src/components/dlz/ui/chip';
import { AttributeType } from '@eliadmin/core/src/stores/category-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** Core `ProductVariants` offers a choice only for attributes with more than one value. */
const isSelectable = (attribute: AttributesEntity) =>
  attribute.type !== AttributeType.RANGE && (attribute.values?.length ?? 0) > 1;

/** Image and size attributes have no picker in the core either. */
const HIDDEN_TYPES: string[] = [AttributeType.IMAGE, AttributeType.SIZE];

const COLOR_TYPES: string[] = [AttributeType.COLOR, AttributeType.MULTICOLOR];

function colorSegments(colors?: string[] | null, color?: string | null) {
  const segments =
    Array.isArray(colors) && colors.length > 0 ? colors : (color ?? '').split(/[\s,;/|]+/);
  return segments.map(segment => segment.trim()).filter(Boolean);
}

/**
 * One attribute: Figma „Varianta“ (Body M Medium) over a row of choice chips. Select, radio and
 * checkbox attributes all pick exactly one value (as in the core), colours are round swatches.
 */
function AttributeChoice({ attribute }: { attribute: AttributesEntity }) {
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const updateSelectedValue = useVariantsStore(state => state.updateSelectedValue);
  const labelId = `variant-attribute-${attribute.id}`;
  const isColor = COLOR_TYPES.includes(attribute.type);
  const chosen = selectedValues.find(selectedValue => selectedValue.attribute.id === attribute.id);

  return (
    <div role="group" aria-labelledby={labelId} className="flex flex-col gap-1">
      <span id={labelId} className="dlz-body-m-medium text-[#0F1012]">
        {attribute.name}
        {isColor && chosen ? (
          <span className="dlz-body-m font-normal text-[#6B7079]"> · {chosen.value.name}</span>
        ) : null}
      </span>
      <div className={cn('flex flex-wrap', isColor ? 'gap-3 py-1' : 'gap-2')}>
        {attribute.values?.map(value => {
          const selected = selectedValues.some(
            selectedValue =>
              selectedValue.attribute.id === attribute.id && selectedValue.value.id === value.id,
          );
          // A second click on the chosen value must not clear it (the store toggles).
          const select = () => {
            if (!selected) updateSelectedValue({ attribute, value });
          };

          if (!isColor) {
            return (
              <ChoiceChip
                key={value.id}
                tone="light"
                selected={selected}
                onSelect={select}
                className="h-10 lg:h-[34px]"
              >
                {value.name}
              </ChoiceChip>
            );
          }

          const segments = colorSegments(value.colors, value.color);
          return (
            <button
              key={value.id}
              type="button"
              aria-pressed={selected}
              aria-label={value.name}
              title={value.name}
              onClick={select}
              style={segments.length < 2 ? { backgroundColor: segments[0] } : undefined}
              className={cn(
                'relative size-8 shrink-0 overflow-hidden rounded-full border border-[#D5D8DC] transition-shadow',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2',
                selected && 'ring-2 ring-[#0F1012] ring-offset-2',
              )}
            >
              {segments.length > 1 ? (
                <span className="absolute inset-0 flex flex-col">
                  {segments.map(segment => (
                    <span key={segment} className="flex-1" style={{ backgroundColor: segment }} />
                  ))}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Variant pickers of the product; the store derives the selected variant from the values. */
export function BuyBoxVariants() {
  const attributes = useVariantsStore(state => state.product.attributes);
  const selectable = (attributes ?? []).filter(
    attribute => isSelectable(attribute) && !HIDDEN_TYPES.includes(attribute.type),
  );
  if (selectable.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      {selectable.map(attribute => (
        <AttributeChoice key={attribute.id} attribute={attribute} />
      ))}
    </div>
  );
}
